/**
 * Monitoring Utilities
 *
 * Helper functions for workflow monitoring and execution analysis.
 */

import type { AgentIterationDataError, MonitoringDataError, NodeExecution } from '@/types/monitoring';

export const getMonitoringDataErrors = (
  dataErrors: readonly MonitoringDataError[] = [],
  toolErrors: readonly AgentIterationDataError[] = [],
): MonitoringDataError[] => [
  ...dataErrors,
  ...toolErrors.map(error => ({
    message: 'Tool details unavailable' as const,
    nodeId: `action-${error.agentName}`,
    iterationName: error.iterationName,
  })),
];

/**
 * Determines if a node execution represents an agent action.
 *
 * Agent actions are identified by:
 * 1. nodeType contains 'agent' (e.g., 'Agent', 'FoundryAgentService')
 * 2. iterationCount > 0 (only agents have iterations, loops use repetitionCount)
 * 3. agentMetadata exists (Foundry-based agents)
 *
 * NOTE: We don't check nodeName as users can rename actions to anything.
 *
 * @param execution - The node execution to check
 * @returns True if the execution represents an agent action
 *
 * @example
 * isAgentExecution({ nodeType: 'Agent', ... }) // true
 * isAgentExecution({ nodeType: 'Action', iterationCount: 3, ... }) // true (if no parentNodeId)
 * isAgentExecution({ nodeType: 'Action', agentMetadata: { ... }, ... }) // true
 * isAgentExecution({ nodeType: 'Compose', iterationCount: 5, parentNodeId: 'For_each', ... }) // false (ForEach inner action)
 * isAgentExecution({ nodeType: 'Foreach', iterationCount: 5, ... }) // false (scope node — uses repetitions, not agent iterations)
 * isAgentExecution({ nodeType: 'Http', ... }) // false
 */
export function isAgentExecution(execution: Pick<NodeExecution, 'nodeType' | 'iterationCount' | 'agentMetadata' | 'parentNodeId'>): boolean {
  // ForEach/Until inner actions have parentNodeId set — they use scope repetitions, not agent repetitions
  if (execution.parentNodeId) {
    return false;
  }

  // Scope nodes use scope repetitions, not agent iterations — never classify as agents
  const nodeTypeLower = execution.nodeType.toLowerCase();
  const scopeNodeTypes = ['foreach', 'until', 'scope', 'if', 'switch'];
  if (scopeNodeTypes.some((scope) => nodeTypeLower === scope)) {
    return false;
  }

  const hasAgentNodeType = nodeTypeLower.includes('agent');
  const hasIterationCount = (execution.iterationCount ?? 0) > 0;
  const hasAgentMetadata = !!execution.agentMetadata;

  return hasAgentNodeType || hasIterationCount || hasAgentMetadata;
}

/**
 * Determines if a node execution has agent iterations available.
 *
 * @param execution - The node execution to check
 * @param fetchedIterations - Optional array of fetched iterations from API
 * @returns True if the execution has iterations available
 */
export function hasAgentIterations(
  execution: Pick<NodeExecution, 'iterationCount' | 'iterations'>,
  fetchedIterations?: unknown[] | null
): boolean {
  return (
    (execution.iterationCount ?? 0) > 0 ||
    (fetchedIterations?.length ?? 0) > 0 ||
    (execution.iterations?.length ?? 0) > 0
  );
}
